import React, { useState } from "react";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Copy, Check, Twitter, Facebook, Linkedin } from "lucide-react";

interface ShareThreadDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  threadTitle: string;
  threadUrl: string;
}

const ShareThreadDialog: React.FC<ShareThreadDialogProps> = ({
  open,
  onOpenChange,
  threadTitle,
  threadUrl,
}) => {
  const [copied, setCopied] = useState(false);

  const handleCopy = async () => {
    try {
      await navigator.clipboard.writeText(threadUrl);
      setCopied(true);
      setTimeout(() => setCopied(false), 1500);
    } catch {
      setCopied(false);
    }
  };

  const encodedUrl = encodeURIComponent(threadUrl);
  const encodedTitle = encodeURIComponent(threadTitle);

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-md">
        <DialogHeader>
          <DialogTitle>Share this thread</DialogTitle>
        </DialogHeader>
        <div className="space-y-4">
          <div>
            <label className="text-sm font-medium mb-1 block">Thread link</label>
            <div className="flex gap-2">
              <Input value={threadUrl} readOnly className="flex-1" />
              <Button
                variant="outline"
                size="icon"
                onClick={handleCopy}
                title="Copy link"
              >
                {copied ? <Check className="h-4 w-4 text-green-600" /> : <Copy className="h-4 w-4" />}
              </Button>
            </div>
            {copied && (
              <div className="text-xs text-green-600 mt-1">Copied!</div>
            )}
          </div>
          <div>
            <label className="text-sm font-medium mb-1 block">Share to</label>
            <div className="flex gap-3">
              <Button
                asChild
                variant="outline"
                size="icon"
                title="Share on Twitter"
              >
                <a
                  href={`https://twitter.com/intent/tweet?url=${encodedUrl}&text=${encodedTitle}`}
                  target="_blank"
                  rel="noopener noreferrer"
                >
                  <Twitter className="h-5 w-5 text-[#1da1f2]" />
                </a>
              </Button>
              <Button
                asChild
                variant="outline"
                size="icon"
                title="Share on Facebook"
              >
                <a
                  href={`https://www.facebook.com/sharer/sharer.php?u=${encodedUrl}`}
                  target="_blank"
                  rel="noopener noreferrer"
                >
                  <Facebook className="h-5 w-5 text-[#1877f3]" />
                </a>
              </Button>
              <Button
                asChild
                variant="outline"
                size="icon"
                title="Share on LinkedIn"
              >
                <a
                  href={`https://www.linkedin.com/shareArticle?mini=true&url=${encodedUrl}&title=${encodedTitle}`}
                  target="_blank"
                  rel="noopener noreferrer"
                >
                  <Linkedin className="h-5 w-5 text-[#0a66c2]" />
                </a>
              </Button>
            </div>
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)}>
            Close
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export default ShareThreadDialog;